/**
 * useWorkflowProgress — the single React hook every UI surface should
 * use to read canonical workflow progress (Round 218A addendum,
 * 2026-08-02).
 *
 * Usage:
 *   const { progress, nextStep, isUnlocked, loading, refresh } =
 *       useWorkflowProgress();
 *
 * The hook aggregates the messy set of backend signals we already have
 * (auth/me, business-brain, brand-brain, offer count, MI/OI versions,
 * created-asset counts) and normalizes them into the single `progress`
 * projection the canonical workflow definition consumes.
 *
 * No component should re-implement "what's next?" logic. Every consumer —
 * dashboard shortcut buttons, onboarding progression, Next Step cards,
 * completion screens, locked/unlocked states, progress indicators,
 * "Start" badges, "Next:" helper text, Dashboard completion percentages,
 * Run My Business, Plan My Week — pulls from here.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import {
  WORKFLOW, getNextStep, isStepUnlocked, projectProgress,
} from '@/config/businessWorkflow';

// One shared in-flight guard so multiple components mounted at once
// don't hammer the backend with the same 6 requests.
let _sharedCache = null;

async function fetchProgress() {
  const safeGet = async (path) => {
    try {
      const r = await api.get(path);
      return r?.data || null;
    } catch (_) { return null; }
  };

  const [me, brandBrain, mi, oi, myCreations] = await Promise.all([
    safeGet('/auth/me'),
    safeGet('/brand-brain'),
    safeGet('/market-intelligence/dashboard-summary'),
    safeGet('/opportunity-intelligence/dashboard-summary'),
    safeGet('/my-creations?limit=200'),
  ]);

  const bb = brandBrain?.brand_brain || null;
  const brandComplete = !!(
    bb && (
      (Array.isArray(bb.primary_colors) && bb.primary_colors.length > 0) ||
      bb.brand_voice ||
      (Array.isArray(bb.brand_personality) && bb.brand_personality.length > 0)
    )
  );

  const creations = myCreations?.creations || myCreations?.items || [];
  const countByKind = (kind) =>
    creations.filter((c) => (c.kind || c.type || '').toLowerCase().includes(kind)).length;

  return projectProgress(
    { ...(me || {}), brand_brain_complete: brandComplete },
    {
      market_intelligence_version: mi?.version || null,
      opportunity_intelligence_version: oi?.version || null,
      sales_pages_count: countByKind('sales_page'),
      lead_magnets_count: countByKind('lead_magnet'),
      emails_count: countByKind('email'),
      ebooks_count: countByKind('ebook'),
      content_pieces_count: countByKind('content'),
      essentials_created: creations.some((c) =>
        ['business_card', 'flyer', 'brochure', 'proposal'].includes((c.kind || '').toLowerCase()),
      ),
      brand_brain_complete: brandComplete,
    },
  );
}

export default function useWorkflowProgress({ shared = true } = {}) {
  const [progress, setProgress] = useState(shared ? _sharedCache : null);
  const [loading, setLoading] = useState(!progress);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const p = await fetchProgress();
      if (shared) _sharedCache = p;
      setProgress(p);
    } finally {
      setLoading(false);
    }
  }, [shared]);

  useEffect(() => {
    if (!progress) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const p = progress || {};
  const nextStep = progress ? getNextStep(p) : null;
  const isUnlocked = useCallback((stepId) => {
    const step = WORKFLOW.find((s) => s.id === stepId);
    return step ? isStepUnlocked(step, p) : false;
  }, [progress]); // eslint-disable-line react-hooks/exhaustive-deps

  // Completion percentage — how many workflow steps are done out of
  // total (excludes ongoing 'growth' step from the denominator).
  const completable = WORKFLOW.filter((s) => s.id !== 'growth');
  const doneCount = completable.filter((s) => s.completedWhen(p)).length;
  const completionPercent = Math.round((doneCount / completable.length) * 100);

  return {
    progress: p,
    nextStep,
    isUnlocked,
    completionPercent,
    doneCount,
    totalCount: completable.length,
    loading,
    refresh,
  };
}
